import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const { user, session } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!user || !session) { setCartItems([]); setCartCount(0); setCartTotal(0); return; }
    try {
      const res = await api.get('/cart');
      const items = res.data.items || [];
      setCartItems(items);
      setCartCount(items.reduce((acc, i) => acc + i.quantity, 0));
      setCartTotal(res.data.total || 0);
    } catch { }
  }, [user, session]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (productId, variantName, quantity = 1) => {
    await api.post('/cart/items', { product_id: productId, variant_name: variantName, quantity });
    await fetchCart();
  };

  const updateQuantity = async (itemId, quantity) => {
    await api.put(`/cart/items/${itemId}?quantity=${quantity}`);
    await fetchCart();
  };

  const removeItem = async (itemId) => {
    await api.delete(`/cart/items/${itemId}`);
    await fetchCart();
  };

  const clearCart = async () => {
    await api.delete('/cart');
    await fetchCart();
  };

  return (
    <CartContext.Provider value={{ cartItems, cartCount, cartTotal, addToCart, updateQuantity, removeItem, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
